/**
 * =========================================================================================
 * Component - Button (Consolidated & Backward Compatible)
 * =========================================================================================
 **/

import React, { useMemo } from 'react';
import { isMobileDevice } from "../common/function";
import * as Icon from 'ubs-icons';
import PropTypes from "prop-types";
import { COLOR } from '../common/constant';

const UButton = (props) => {
    const isMobileView = useMemo(() => {
        return isMobileDevice() || window.innerWidth <= 768;
    }, []);

    const shouldAddMobileClass = useMemo(() => {
        if (props?.mobile !== undefined) return !!props?.mobile;
        if (!isMobileView) return false;
        
        if (props?.type) {
            const mobileClassTypes = [
                'discard', 'cancel', 'delete', 'save', 'update', 'approve', 'submit',
                'reject', 'copy', 'edit', 'search', 'back', 'next', 'download',
                'send', 'preview', 'share', 'save draft', 'connect', 'connected'
            ];
            return mobileClassTypes.includes(props?.type?.toLowerCase());
        }
        return false;
    }, [isMobileView, props?.mobile, props?.type]);

    const classNames = useMemo(() => {
        const classes = ['u_btn'];

        classes.push('u_text-c');
        if (props?.large) {
            classes.push('u_btn__largeBtn');
        } else {
            classes.push('u_btn__smallBtn');
            classes.push('small');
        }

        if (props?.cn) classes.push(props.cn);

        if (props?.variant) {
            classes.push(props.variant);
            classes.push(`u_btn__${props.variant}`);
        }

        if (shouldAddMobileClass) {
            classes.push('u_btn-mobile');
        }

        if (props?.loading) {
            classes.push('loading');
            classes.push('u_btn__loading');
        }

        return classes.join(' ');
    }, [props?.cn, props?.variant, props?.loading, props?.large, shouldAddMobileClass]);

    const icon = useMemo(() => {
        const typeStr = typeof props?.type === 'string' ? props?.type?.toLowerCase() : '';
        
        if (['discard', 'cancel', 'delete', 'reject'].includes(typeStr)) {
            const isReject = typeStr === 'reject';
            return <Icon.Close fill={isReject ? COLOR.u_white : COLOR.u_font} size={12} />;
        }
        else if (['save', 'update', 'approve', 'submit'].includes(typeStr)) {
            return <Icon.CheckMark fill={COLOR.u_white} size={14} />;
        }
        else if (['copy'].includes(typeStr)) {
            return <Icon.Copy fill={COLOR.u_white} size={12} />;
        }
        else if (['edit'].includes(typeStr)) {
            return <Icon.Edit fill={COLOR.u_white} size={16} />;
        }
        else if (['search'].includes(typeStr)) {
            return <Icon.Search fill={COLOR.u_white} size={16} />;
        }
        else if (['back'].includes(typeStr)) {
            return <Icon.LeftArrow fill={COLOR.u_font} size={16} />;
        }
        else if (['next'].includes(typeStr)) {
            return <Icon.RightArrow fill={COLOR.u_white} size={16} />;
        }
        else if (['download'].includes(typeStr)) {
            return <Icon.Download fill={COLOR.u_white} size={16} />;
        }
        else if (['send', 'share'].includes(typeStr)) {
            return <Icon.SendMessage fill={COLOR.u_white} size={16} />;
        }
        else if (['preview'].includes(typeStr)) {
            return <Icon.Eye fill={COLOR.u_white} size={16} />;
        }
        else if (['save draft'].includes(typeStr)) {
            return <Icon.Info fill={COLOR.u_blue} size={16} />;
        }
        else if (['connect'].includes(typeStr)) {
            return <Icon.Link fill={COLOR.u_blue} size={16} />;
        }
        else if (['connected'].includes(typeStr)) {
            return <Icon.Link fill={COLOR.u_white} size={16} />;
        }
        
        return props?.type;
    }, [props?.type]);

    const renderedContent = useMemo(() => {
        if (isMobileView) {
            if (props?.title) return props?.title;
            return icon;
        }
        return props?.children || props?.title || props?.type;
    }, [isMobileView, props?.children, props?.title, props?.type, icon]);

    const buttonType = useMemo(() => {
        if (props?.isSubmit !== undefined) {
            return props?.isSubmit ? 'submit' : 'button';
        }
        return props?.btnType || 'button';
    }, [props?.isSubmit, props?.btnType]);

    return (
        <React.Fragment>
            <button
                type={buttonType}
                className={classNames}
                disabled={props?.disabled}
                onClick={props?.onClick}
                style={props?.style}
                data-testid={props?.testId || props?.['data-testid']}
            >
                {renderedContent}
                {props?.loading && <span className="u_btn__loader" />}
            </button>
        </React.Fragment>
    );
};

UButton.propTypes = {
    cn: PropTypes.string,
    variant: PropTypes.string,
    type: PropTypes.string,
    btnType: PropTypes.string,
    isSubmit: PropTypes.bool,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    mobile: PropTypes.any,
    large: PropTypes.bool,
    onClick: PropTypes.func,
    style: PropTypes.object,
    title: PropTypes.string,
    testId: PropTypes.string,
    children: PropTypes.node,
};

UButton.defaultProps = {
    cn: '',
    variant: 'blue',
    type: '',
    btnType: 'button',
    isSubmit: undefined,
    loading: false,
    disabled: false,
    mobile: undefined,
    large: false,
    onClick: () => {},
    style: undefined,
    title: '',
    testId: undefined,
};

export default UButton;